import * as React from 'react';

import type {IconAsset} from '@sail/icons/types';

import type {ButtonProps as SailButtonProps} from '@sail/ui';
import {css, createViewConfig, Button as SailButton, Icon} from '@sail/ui';
import {getButtonPressProps} from '../../utils/interaction';

const NATIVE_BUTTON_TYPES: Array<Exclude<SailButtonProps['type'], undefined>> =
  ['primary', 'secondary', 'destructive'];
type NATIVE_BUTTON_TYPE = (typeof NATIVE_BUTTON_TYPES)[number];

function isNativeButtonType(
  str: string | undefined,
): str is NATIVE_BUTTON_TYPE {
  return NATIVE_BUTTON_TYPES.indexOf(str as any) !== -1;
}

type StyledButtonProps = Omit<SailButtonProps, 'type'> & {
  type?: 'primary' | 'secondary' | 'destructive' | 'icon';
  maximized?: true | false;
};

const StyledButtonConfig = createViewConfig({
  name: 'StyledButton',
  props: {} as StyledButtonProps,
  defaults: {
    type: 'secondary',
    size: 'medium',
    maximized: false,
  },
});

const StyledButton = StyledButtonConfig.createView(
  ({type, ...props}) => {
    const buttonType = isNativeButtonType(type) ? type : undefined;
    return <SailButton {...props} type={buttonType} />;
  },
  {
    forward: {
      type: true,
    },
    variants: {
      maximized: {
        true: css({
          width: 'fill',
        }),
      },

      type: {
        icon: css({
          color: 'icon',
          backgroundColor: 'transparent',
          keyline: 'transparent !important',
          paddingTop: '0px !important',
          paddingBottom: '0px !important',
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
          // set the min width and height to 24px for button accessibility
          minHeight: '24px !important',
          minWidth: '24px !important',
          // here we override the hover and active design tokens so the icon button doesn't change color on hover or active
          ':hover': {
            backgroundColor: 'transparent !important',
            keyline: 'transparent !important',
            color: 'icon',
          },
          ':active': {
            backgroundColor: 'transparent !important',
            keyline: 'transparent !important',
            color: 'icon',
          },
        }),
        // TODO (SAIL-3812): Sail uses keylines (shadows) for borders which causes buttons to exceed width of their containers
        // This uses a normal border for primary/secondary buttons instead of keylines.
        primary: css({
          borderWidth: 'small',
          borderStyle: 'solid',
        }),
        secondary: css({
          borderWidth: 'small',
          borderStyle: 'solid',
        }),
        destructive: css({
          borderWidth: 'small',
          borderStyle: 'solid',
        }),
      },
    },
    css: {
      stack: 'x',
      alignX: 'center',
      alignY: 'center',
      boxShadow: '0 0 0 0 transparent !important',
      topShadow: '0 0 0 0 transparent !important',

      // TODO (SAIL-3812): Sail uses keylines (shadows) for borders which causes buttons to exceed width of their containers
      // This removes the keylines in favor for a normal border
      keyline: 'transparent !important',
    },
  },
);

type PendingProps = {
  icon?: IconAsset;
  iconPosition?: 'right' | 'left';
  pending?: boolean;
  enabledWhenPending?: boolean;
};

export type ButtonProps = Omit<
  React.ComponentProps<typeof SailButton>,
  'type'
> & {
  type?: 'primary' | 'secondary' | 'destructive' | 'icon';
  size?: 'small' | 'medium' | 'large';
  maximized?: true | false;
} & PendingProps;

export const Button: React.FC<ButtonProps> = React.forwardRef(
  (
    {
      children,
      disabled,
      icon,
      iconPosition = 'left',
      maximized,
      pending,
      type,
      uses = [],
      enabledWhenPending,
      ...buttonProps
    },
    ref,
  ): JSX.Element => {
    const renderIcon = () => {
      if (!pending && !icon) return null;
      if (icon) return <Icon icon={icon} size="xsmall" />;
    };

    const isDisabled = disabled || (pending && !enabledWhenPending);

    return (
      <StyledButton
        {...buttonProps}
        disabled={isDisabled}
        pending={pending}
        maximized={!!maximized}
        uses={uses}
        type={type}
        {...getButtonPressProps(buttonProps.onPress as any, isDisabled)}
        ref={ref}
      >
        {iconPosition === 'left' && renderIcon()}
        {children}
        {iconPosition === 'right' && renderIcon()}
      </StyledButton>
    );
  },
);
